import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import { config, EAppEnv } from 'affiny-core/env';
import { ILoggerOption, ILoggerStrategy } from 'affiny-core/logger';
import { ELoggerCategory, ELoggerLevel } from 'affiny-core/logger.types';

const SENTRY_LEVELS: Record<ELoggerLevel, Sentry.Severity> = {
  [ELoggerLevel.Debug]: Sentry.Severity.Debug,
  [ELoggerLevel.Error]: Sentry.Severity.Error,
  [ELoggerLevel.Fatal]: Sentry.Severity.Fatal,
  [ELoggerLevel.Info]: Sentry.Severity.Info,
  [ELoggerLevel.Warning]: Sentry.Severity.Warning,
};

const integrations = [
  new Integrations.Dedupe(),
  new Integrations.ExtraErrorData({}),
  new Integrations.ReportingObserver(),
  new Integrations.RewriteFrames(),
  new Integrations.Transaction(),
] as any[];

export class LoggerWebStrategy implements ILoggerStrategy {
  private static async initializeSentry() {
    Sentry.init({
      dsn: config.env.credentials.sentryDSN,
      ignoreErrors: [
        /^Syntax error, unrecognized expression: .banner-close-button, .optanon-alert-box-button-container/,
        /^Not logged in/,
        /^affiny-core\/services\/tracking\/tracker.ts/,
      ],
      integrations,
    });
    Sentry.configureScope(scope => {
      scope.clear();
      scope.setTag('x-client-id', config.env.meeticApi.xClientId);
      scope.setTag('locale', config.env.locale);
    });
  }
  private static getSentryLevel(level: ELoggerLevel) {
    return SENTRY_LEVELS[level] || Sentry.Severity.Error;
  }

  private static getSentryOption(options: ILoggerOption) {
    if (options.level) {
      return LoggerWebStrategy.getSentryLevel(options.level);
    }

    return undefined;
  }

  constructor() {
    // Remove this condition if you want to have an error listed in affiny-wam-local
    // You need to change affiny-wam-prod by affiny-wam-local in sentry.prod.properties at the root of affiny-mobile
    if (config.env.envName !== EAppEnv.local) {
      LoggerWebStrategy.initializeSentry();
    }
  }

  public addHint(
    category: ELoggerCategory,
    message: string,
    options: ILoggerOption
  ) {
    const breadcrumb: Sentry.Breadcrumb = { category, message };

    if (!options.level) {
      breadcrumb.level = options.level;
    }

    Sentry.addBreadcrumb(breadcrumb);
  }

  public enrichWithTag(key: string, value: string) {
    Sentry.configureScope(scope => {
      scope.setTag(key, value);
    });
  }

  public enrichWithUser(user: { id: string }): void {
    Sentry.configureScope(scope => {
      scope.setUser(user);
    });
  }

  public handleException(exception: Error, extraInfo: object) {
    Sentry.withScope(scope => {
      Object.entries(extraInfo).map(([key, value]) =>
        scope.setExtra(key, value)
      );
      Sentry.captureException(exception);
    });
  }

  public log(message: string, options: ILoggerOption) {
    Sentry.captureMessage(message, LoggerWebStrategy.getSentryOption(options));
  }

  public logWithMessageAndData(
    message: string,
    data: unknown,
    options: ILoggerOption = {}
  ) {
    Sentry.withScope(scope => {
      scope.setLevel(
        LoggerWebStrategy.getSentryLevel(options.level || ELoggerLevel.Error)
      );
      scope.setExtra('Payload', data);
      this.log(message, options);
    });
  }
}
